import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import theme from '@/styles/theme';
import Modal from '@/components/common/common-modal';
import Message from '@/constant/message';
import { getPolicyHtml } from '@/fetches/common/get-policy-html';
import getHTMLContent from '@/utils/get-html-content';
import { EAuthModalPage } from '@/store/types';
import { actions } from '@/store';
import useSelectorUser from '@/hooks/use-selector-user';
import media from '@/styles/media';
import {
  Wrapper, MainDiv, UpperDiv, HeaderButtonDiv,
} from '../common/new-auth-layout';
import { BasicButton, BasicButtonLabel } from '../common/new-auth-button';
import { Checkbox } from '../common/checkbox';
import { Logo } from '../common/auth-layout/logo';
import MobileLogo from '../common/auth-layout/mobile-logo';

export const AgreePolicy = () => {
  const { register, confirmCodeType, gotoAuthLogin } = useSelectorUser('authModal');
  const [termsData, setTermsData] = useState('');
  const [policyData, setPolicyData] = useState('');
  const [checkTerms, setCheckTerms] = useState(false);
  const [checkPolicy, setCheckPolicy] = useState(false);

  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    getPolicyHtml('terms', i18n.language).then(setTermsData);
    getPolicyHtml('private', i18n.language).then(setPolicyData);
  }, [i18n.language]);

  const gotoLogin = useCallback(() => {
    if (gotoAuthLogin) {
      gotoAuthLogin();
    }
    // dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.INPUT_EMAIL));
  }, [gotoAuthLogin]);

  const goBack = useCallback(() => {
    dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SELECT_USER));
  }, [dispatch]);

  const onChangeCheckTerm = useCallback(() => {
    setCheckTerms(!checkTerms);
  }, [checkTerms]);

  const onChangeCheckPolicy = useCallback(() => {
    setCheckPolicy(!checkPolicy);
  }, [checkPolicy]);

  const onClickOKButton = useCallback(() => {
    dispatch(actions.user.authModal.setPolicy(true));
    if (register === 'general') {
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SIGN_UP));
    } else {
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.CREATOR_SIGN_UP));
    }
  }, [dispatch, register]);

  return (
    <Modal show closeOnClickOverlay={false} backgroundOpacity={1}>
      <Wrapper>
        <Container data-scroll>
          <UpperDiv>
            <Logo />
            <HeaderButtonDiv>
              <BasicButtonLabel>
                {`${t(Message.이미계정이있으신가요)}`}
              </BasicButtonLabel>
              <BasicButton
                onClick={gotoLogin}
                className="signUpPage-login"
              >
                {t(Message.로그인)}
              </BasicButton>
            </HeaderButtonDiv>
          </UpperDiv>
          <MobileLogoWrap>
            <MobileLogo />
          </MobileLogoWrap>
          <Main>
            <TextContainer>
              <Title>{t(Message.이용약관)}</Title>
              <Text>{t(Message.서비스이용을위해약관동의가필요합니다)}</Text>
            </TextContainer>
            <PolicyContainer checked={checkTerms}>
              <PolicyDiv><Policy dangerouslySetInnerHTML={getHTMLContent(termsData)} data-scroll /></PolicyDiv>
              <ChechboxContainer>
                <Checkbox
                  text={t(Message.STEAMuP서비스이용약관동의)}
                  onChange={onChangeCheckTerm}
                  checked={checkTerms}
                />
              </ChechboxContainer>
            </PolicyContainer>
            <PolicyContainer checked={checkPolicy}>
              <PolicyDiv><Policy dangerouslySetInnerHTML={getHTMLContent(policyData)} data-scroll /></PolicyDiv>
              <ChechboxContainer>
                <Checkbox
                  text={t(Message.개인정보수집및이용에동의)}
                  onChange={onChangeCheckPolicy}
                  checked={checkPolicy}
                />
              </ChechboxContainer>
            </PolicyContainer>
            <ButtonContainer>
              <Button onClick={goBack}>{t(Message.이전)}</Button>
              <Button
                active
                disabled={!(checkPolicy && checkTerms)}
                onClick={confirmCodeType === 'social' ? gotoLogin : onClickOKButton}
              >
                {t(Message.확인)}
              </Button>
            </ButtonContainer>

          </Main>
        </Container>
      </Wrapper>
    </Modal>
  );
};

const ChechboxContainer = styled.div`
  width: 100%;
  padding: 0 5px;
  width: fit-content;
`;

const Container = styled(MainDiv)`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  ${media.lg`
    align-items: center;
    max-height: 100%;
    overflow: hidden auto;
    margin:0;
    border-radius: unset;
  `}
`;

const MobileLogoWrap = styled.div`
  ${media.lg`
    padding: 30px 0 30px 17px;
    width: 100%;
    max-width: 420px;
  `}
  ${media.xs`
    padding: 30px 0 30px 25px;
  `}
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 153px;
  gap: 12px;
  ${media.lg`
    padding: 0px 15px 50px;
    max-width: 420px;
  `}
`;

const TextContainer = styled.div`
`;

const Title = styled.div`
  font-family: ${theme.fontFamily.semiBold};
  font-size: 30px;
  line-height: 36px;
  padding-bottom: 22px;
  ${media.xs`
    font-size: 26px;
    line-height: 31px;
  `}
  ${media.lg`
    text-align: center;
  `}
`;

const Text = styled.div`
  font-family: ${theme.fontFamily.regular};
  font-size: 14px;
  line-height: 22px;
  ${media.lg`
    font-family: ${theme.fontFamily.medium};
    text-align: center;
    padding-bottom: 22px;
  `}
`;
interface PolicyContainerProps {
  checked: boolean
}
const PolicyContainer = styled.div<PolicyContainerProps>`
  color: ${(props) => props.checked && theme.color.purple};
`;

const PolicyDiv = styled.div`
  border: 2px solid ${theme.color.border};
  border-radius: 8px;
  padding: 6px;
  background: #FFFFFF;
  color: black;
`;

const Policy = styled.div`
  overflow: auto;
  box-sizing: border-box;
  font-family: ${theme.fontFamily.regular};
  width: 100%;
  max-width: 1094px;
  max-height: 185px;
  padding: 34px;
  
  ${media.lg`
    padding: 0px 18px;
    line-height: 22px;
  `}
  @media(max-width: 340px){
    padding: 0 5px;
  }
  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    width: 10px;
    height: 10px;
    background: #D9D9D9;

  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.color.purple};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
`;

interface ButtonProps {
  active?: boolean
}
const Button = styled.button<ButtonProps>`
  font-family:  ${theme.fontFamily.regular};
  font-size: 16px;
  line-height: 19px;
  color: #FFFFFF;
  background-color: ${(props) => (props.active ? theme.color.purple : '#4B586A')};
  border: none;
  border-radius: 8px;
  padding: 16px 85px;
  cursor: pointer;

  :disabled{
    border: none;
    background-color: #F0F0F0;
    color: ${theme.color.darkGray};
  }

  &:hover{
    opacity: 0.9;
  }

  ${media.lg`
    padding: 16px 0;
    flex: 1;
  `}
`;
